import React, { useState, useEffect } from "react";
import {
  Sparkles,
  ArrowRight,
  Twitter,
  Github,
  Linkedin,
  Instagram,
  Facebook,
  Youtube,
  Terminal,
  MessageCircle
} from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { CodeSlot } from "./common/CodeSlot";
import { FEATURES_HUB_PATH } from "../src/content/featurePages";
import { useStore } from "../src/lib/store";

// ─── Shared Components ───────────────────────────────────────────────────────

const RedditIcon = ({ size = 24, className = "" }: { size?: number, className?: string }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 512 512" 
    xmlns="http://www.w3.org/2000/svg" 
    fillRule="evenodd" 
    clipRule="evenodd" 
    strokeLinejoin="round" 
    strokeMiterlimit="2"
    className={className}
  >
    <circle cx="-892" cy="179" r="262" fill="currentColor" transform="matrix(.9771 0 0 .9771 1127.57 81.1)"/>
    <g transform="translate(1.249 1.608) scale(1.99024)">
      <circle cx="200.6" cy="123.7" r="29.9" fill="url(#prefix___Radial1)"/>
      <circle cx="55.4" cy="123.7" r="29.9" fill="url(#prefix___Radial2)"/>
      <ellipse cx="128.1" cy="149.3" rx="85.3" ry="64" fill="url(#prefix___Radial3)"/>
      <path d="M102.8 143.1c-.5 10.8-7.7 14.8-16.1 14.8-8.4 0-14.8-5.6-14.3-16.4.5-10.8 7.7-18 16.1-18 8.4 0 14.8 8.8 14.3 19.6zM183.6 141.5c.5 10.8-5.9 16.4-14.3 16.4s-15.6-3.9-16.1-14.8c-.5-10.8 5.9-19.6 14.3-19.6s15.6 7.1 16.1 18z" fill="#1A2B3C" fillRule="nonzero"/>
      <path d="M153.3 144.1c.5 10.1 7.2 13.8 15 13.8s13.8-5.5 13.4-15.7c-.5-10.1-7.2-16.8-15-16.8s-13.9 8.5-13.4 18.7z" fill="url(#prefix___Radial4)" fillRule="nonzero"/>
      <path d="M102.8 144.1c-.5 10.1-7.2 13.8-15 13.8s-13.8-5.5-13.3-15.7c.5-10.1 7.2-16.8 15-16.8s13.8 8.5 13.3 18.7z" fill="url(#prefix___Radial5)" fillRule="nonzero"/>
      <path d="M128.1 165.1c-10.6 0-20.7.5-30.1 1.4-1.6.2-2.6 1.8-2 3.2 5.2 12.3 17.6 21 32.1 21s26.8-8.6 32.1-21c.6-1.5-.4-3.1-2-3.2-9.4-.9-19.5-1.4-30.1-1.4z" fill="#bbcfda" fillRule="nonzero"/>
      <path d="M128.1 167.5c-10.6 0-20.7.5-30 1.5-1.6.2-2.6 1.8-2 3.3 5.2 12.5 17.6 21.3 32 21.3 14.4 0 26.8-8.8 32-21.3.6-1.5-.4-3.1-2-3.3-9.4-1-19.5-1.5-30-1.5z" fill="#fff" fillRule="nonzero"/>
      <path d="M128.1 166.2c-10.4 0-20.3.5-29.5 1.4-1.6.2-2.6 1.8-2 3.2 5.2 12.3 17.3 21 31.5 21s26.3-8.6 31.5-21c.6-1.5-.4-3.1-2-3.2-9.2-.8-19.1-1.4-29.5-1.4z" fill="url(#prefix___Radial6)" fillRule="nonzero"/>
      <circle cx="174.8" cy="55.5" r="21.2" fill="url(#prefix___Radial7)"/>
      <path d="M127.8 88c-2.5 0-4.6-1.1-4.6-2.7 0-19 15.4-34.4 34.4-34.4 2.5 0 4.6 2.1 4.6 4.6 0 2.5-2.1 4.6-4.6 4.6-13.9 0-25.2 11.3-25.2 25.2 0 1.7-2.1 2.7-4.6 2.7z" fill="url(#prefix___Radial8)" fillRule="nonzero"/>
      <path d="M97.3 149.1c0 3.9-4.2 5.7-9.3 5.7-5.1 0-9.3-1.8-9.3-5.7 0-3.9 4.2-7.1 9.3-7.1 5.1 0 9.3 3.1 9.3 7.1zM177.5 149.1c0 3.9-4.2 5.7-9.3 5.7-5.1 0-9.3-1.8-9.3-5.7 0-3.9 4.2-7.1 9.3-7.1 5.1 0 9.3 3.1 9.3 7.1z" fill="#10B981" fillRule="nonzero"/>
      <ellipse cx="94.4" cy="134.8" rx="3.3" ry="3.6" fill="#ffc49c"/>
      <ellipse cx="173.3" cy="134.8" rx="3.3" ry="3.6" fill="#ffc49c"/>
    </g>
    <defs>
      <radialGradient id="prefix___Radial1" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="matrix(59.9015 0 0 -52.2545 201.012 107.557)">
        <stop offset="0" stopColor="#feffff"/><stop offset=".4" stopColor="#feffff"/><stop offset=".51" stopColor="#f9fcfc"/><stop offset=".62" stopColor="#edf3f5"/><stop offset=".7" stopColor="#dee9ec"/><stop offset=".72" stopColor="#d8e4e8"/><stop offset=".76" stopColor="#ccd8df"/><stop offset=".8" stopColor="#c8d5dd"/><stop offset=".83" stopColor="#ccd6de"/><stop offset=".85" stopColor="#d8dbe2"/><stop offset=".88" stopColor="#ede3e9"/><stop offset=".9" stopColor="#ffebef"/><stop offset="1" stopColor="#ffebef"/>
      </radialGradient>
      <radialGradient id="prefix___Radial2" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="matrix(59.9015 0 0 -52.2545 55.892 107.557)">
        <stop offset="0" stopColor="#feffff"/><stop offset=".4" stopColor="#feffff"/><stop offset=".51" stopColor="#f9fcfc"/><stop offset=".62" stopColor="#edf3f5"/><stop offset=".7" stopColor="#dee9ec"/><stop offset=".72" stopColor="#d8e4e8"/><stop offset=".76" stopColor="#ccd8df"/><stop offset=".8" stopColor="#c8d5dd"/><stop offset=".83" stopColor="#ccd6de"/><stop offset=".85" stopColor="#d8dbe2"/><stop offset=".88" stopColor="#ede3e9"/><stop offset=".9" stopColor="#ffebef"/><stop offset="1" stopColor="#ffebef"/>
      </radialGradient>
      <radialGradient id="prefix___Radial3" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="matrix(180.687 0 0 -126.865 130.347 99.176)">
        <stop offset="0" stopColor="#feffff"/><stop offset=".4" stopColor="#feffff"/><stop offset=".51" stopColor="#f9fcfc"/><stop offset=".62" stopColor="#edf3f5"/><stop offset=".7" stopColor="#dee9ec"/><stop offset=".72" stopColor="#d8e4e8"/><stop offset=".76" stopColor="#ccd8df"/><stop offset=".8" stopColor="#c8d5dd"/><stop offset=".83" stopColor="#ccd6de"/><stop offset=".85" stopColor="#d8dbe2"/><stop offset=".88" stopColor="#ede3e9"/><stop offset=".9" stopColor="#ffebef"/><stop offset="1" stopColor="#ffebef"/>
      </radialGradient>
      <radialGradient id="prefix___Radial4" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="matrix(-15.0964 0 0 22.1628 165.28 150.971)">
        <stop offset="0" stopColor="#34D399"/><stop offset=".5" stopColor="#10B981"/><stop offset=".7" stopColor="#059669"/><stop offset=".82" stopColor="#047857"/><stop offset=".92" stopColor="#065f46"/><stop offset="1" stopColor="#064e3b"/>
      </radialGradient>
      <radialGradient id="prefix___Radial5" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="matrix(15.0964 0 0 22.1628 90.19 150.971)">
        <stop offset="0" stopColor="#34D399"/><stop offset=".5" stopColor="#10B981"/><stop offset=".7" stopColor="#059669"/><stop offset=".82" stopColor="#047857"/><stop offset=".92" stopColor="#065f46"/><stop offset="1" stopColor="#064e3b"/>
      </radialGradient>
      <radialGradient id="prefix___Radial6" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="matrix(53.2322 0 0 -35.1106 128.369 194.908)">
        <stop offset="0" stopColor="#172e35"/><stop offset=".29" stopColor="#0e1c21"/><stop offset=".73" stopColor="#030708"/><stop offset="1" stopColor="#000000"/>
      </radialGradient>
      <radialGradient id="prefix___Radial7" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="matrix(46.7274 0 0 -46.7274 175.312 34.106)">
        <stop offset="0" stopColor="#feffff"/><stop offset=".4" stopColor="#feffff"/><stop offset=".51" stopColor="#f9fcfc"/><stop offset=".62" stopColor="#edf3f5"/><stop offset=".7" stopColor="#dee9ec"/><stop offset=".72" stopColor="#d8e4e8"/><stop offset=".76" stopColor="#ccd8df"/><stop offset=".8" stopColor="#c8d5dd"/><stop offset=".83" stopColor="#ccd6de"/><stop offset=".85" stopColor="#d8dbe2"/><stop offset=".88" stopColor="#ede3e9"/><stop offset=".9" stopColor="#ffebef"/><stop offset="1" stopColor="#ffebef"/>
      </radialGradient>
      <radialGradient id="prefix___Radial8" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="matrix(38.3003 0 0 -38.3003 155.84 85.046)">
        <stop offset="0" stopColor="#7a9299"/><stop offset=".48" stopColor="#7a9299"/><stop offset=".67" stopColor="#172e35"/><stop offset=".75" stopColor="#000000"/><stop offset=".82" stopColor="#172e35"/><stop offset="1" stopColor="#172e35"/>
      </radialGradient>
    </defs>
  </svg>
);

const TikTokIcon = ({ size = 24, className = "" }: { size?: number, className?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="currentColor" className={className}>
    <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
  </svg>
);

const ProductHuntIcon = ({ size = 24, className = "" }: { size?: number, className?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="currentColor" className={className}>
    <path d="M13.604 8.4h-3.405V12h3.405a1.8 1.8 0 100-3.6zM12 0C5.372 0 0 5.372 0 12s5.372 12 12 12 12-5.372 12-12S18.628 0 12 0zm1.604 13.8h-3.405V18H7.801V6h5.803a3.6 3.6 0 010 7.2z" />
  </svg>
);

const Magnetic = ({ children }: { children: React.ReactElement }) => {
  const ref = React.useRef<HTMLDivElement>(null);

  const handleMouseMove = (e: React.MouseEvent) => {
    const { clientX, clientY } = e;
    const { left, top, width, height } = ref.current?.getBoundingClientRect() || { left: 0, top: 0, width: 0, height: 0 };
    const x = clientX - (left + width / 2);
    const y = clientY - (top + height / 2);
    if (ref.current) {
      ref.current.style.transform = `translate(${x * 0.3}px, ${y * 0.3}px)`;
    }
  };

  const handleMouseLeave = () => {
    if (ref.current) {
      ref.current.style.transform = `translate(0px, 0px)`;
    }
  };

  return (
    <div 
      ref={ref} 
      onMouseMove={handleMouseMove} 
      onMouseLeave={handleMouseLeave}
      className="transition-transform duration-300 ease-out"
    >
      {children}
    </div>
  );
};

// ─── Footer Component ────────────────────────────────────────────────────────

export const Footer = () => {
  const { t } = useTranslation();
  const location = useLocation();

  const getLLMLink = () => {
    const path = location.pathname;
    if (path === "/") return { url: "/llms.txt", label: "llms.txt" };

    // Explicit static mappings
    const staticMappings: Record<string, string> = {
      "/about": "about.md",
      "/brand": "brand-protocol.md",
      "/deck": "pitch-deck.md",
      "/privacy": "privacy-policy.md",
      "/terms": "terms-of-service.md",
      "/plu": "plu-registry.md",
      "/units": "unit-registry.md",
      "/faq": "faq.md",
      "/architect": "architect-enrollment.md",
      "/partners": "merchant-partners.md",
      "/pricing": "pricing.md",
      "/sign-in": "sign-in.md",
      "/sign-up": "sign-up.md"
    };

    let file = staticMappings[path];

    // Dynamic prefixes
    if (!file) {
      if (path === FEATURES_HUB_PATH || path.startsWith(`${FEATURES_HUB_PATH}/`)) {
        file = "features.md";
      } else if (path === "/alternatives" || path === "/alternatives/" || path.startsWith("/alternatives/")) {
        file = "alternatives.md";
      } else if (path.startsWith("/history/")) {
        file = "logbook.md";
      } else if (path.startsWith("/stores/cheapest-in/")) {
        file = "discovery-stores.md";
      } else if (path.startsWith("/prices/")) {
        file = "discovery-prices.md";
      } else if (path === "/blog" || path.startsWith("/blog/")) {
        file = "blog.md";
      } else if (path === "/newsroom" || path.startsWith("/newsroom/")) {
        file = "newsroom.md";
      }
    }

    if (!file) return null;
    return { url: `/llms/${file}`, label: file };
  };

  const llmNode = getLLMLink();

  const [alternatives, setAlternatives] = useState<{ name: string; slug: string }[]>([]);

  useEffect(() => {
    const fetchAlts = async () => {
      try {
        const res = await fetch('/api/alternatives/public/list');
        if (res.ok) {
          const data = await res.json();
          if (Array.isArray(data.alternatives)) {
            setAlternatives(data.alternatives.map((a: any) => ({
              name: a.competitor || a.title || "",
              slug: a.slug || ""
            })));
          }
        }
      } catch (e) {
        console.error("Footer: Alts fetch failed:", e);
      }
    };
    fetchAlts();
  }, []);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Geist+Mono:wght@100..900&family=Instrument+Serif:ital@0;1&family=Outfit:wght@100..900&family=Plus+Jakarta+Sans:ital,wght@0,200..800;1,200..800&display=swap');

        :root {
          --primary: #10B981;
          --charcoal: #0A0F1E;
          --slate: #1A2B3C;
          --ghost: #F8FAFC;
        }

        .font-outfit { font-family: 'Outfit', sans-serif; }
      `}</style>
      <footer className="bg-[var(--charcoal)] text-white pt-14 pb-8 px-6 sm:px-10 lg:px-16 border-t border-white/5 relative z-30 w-full overflow-hidden">
      <div className="max-w-7xl mx-auto flex flex-col gap-10 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)] gap-10 lg:gap-12 items-start text-left">
          <nav itemScope itemType="http://schema.org/SiteNavigationElement" className="grid w-full grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-8 lg:order-2">
            <div className="flex flex-col items-start gap-4 text-left">
              <span className="text-xs font-black uppercase text-white/40 tracking-widest">{t('common.product')}</span>
              <div className="flex flex-col gap-3">
                {/* Same pages as the navbar: Features and Pricing. */}
                <Link itemProp="url" to={FEATURES_HUB_PATH} className="text-sm font-bold hover:text-[var(--primary)] transition-all uppercase text-white/60"><span itemProp="name">{t('landing.features_title')}</span></Link>
                <Link itemProp="url" to="/pricing" className="text-sm font-bold hover:text-[var(--primary)] transition-all uppercase text-white/60"><span itemProp="name">{t('landing.pricing')}</span></Link>
              </div>
            </div>
            <div className="flex flex-col items-start gap-4 text-left">
              <span className="text-xs font-black uppercase text-white/40 tracking-widest">{t('common.resources')}</span>
              <div className="flex flex-col gap-3">
                <Link itemProp="url" to="/plu" className="text-sm font-bold hover:text-[var(--primary)] transition-all uppercase text-white/60"><span itemProp="name">{t('common.commodities')}</span></Link>
                <Link itemProp="url" to="/units" className="text-sm font-bold hover:text-[var(--primary)] transition-all uppercase text-white/60"><span itemProp="name">{t('common.units')}</span></Link>
                <Link itemProp="url" to="/blog" className="text-sm font-bold hover:text-[var(--primary)] transition-all uppercase text-white/60"><span itemProp="name">{t('nav.blog')}</span></Link>
                <Link itemProp="url" to="/newsroom" className="text-sm font-bold hover:text-[var(--primary)] transition-all uppercase text-white/60"><span itemProp="name">{t('nav.newsroom')}</span></Link>
              </div>
            </div>
            <div className="flex flex-col items-start gap-4 text-left">
              <span className="text-xs font-black uppercase text-white/40 tracking-widest">{t('common.comparisons')}</span>
              <div className="flex flex-col gap-3">
                {alternatives.map(alt => (
                  <Link itemProp="url" key={alt.slug} to={`/alternatives/${alt.slug}`} className="text-sm font-bold hover:text-[var(--primary)] transition-all uppercase text-white/60"><span itemProp="name">{alt.name}</span></Link>
                ))}
              </div>
            </div>
            <div className="flex flex-col items-start gap-4 text-left">
              <span className="text-xs font-black uppercase text-white/40 tracking-widest">{t('common.company')}</span>
              <div className="flex flex-col gap-3">
                <Link itemProp="url" to="/deck" className="text-sm font-bold hover:text-[var(--primary)] transition-all uppercase text-white/60"><span itemProp="name">{t('menu.pitch_deck')}</span></Link>
                <Link itemProp="url" to="/about" className="text-sm font-bold hover:text-[var(--primary)] transition-all uppercase text-white/60"><span itemProp="name">{t('nav.about')}</span></Link>
                <Link itemProp="url" to="/faq" className="text-sm font-bold hover:text-[var(--primary)] transition-all uppercase text-white/60"><span itemProp="name">{t('nav.faq')}</span></Link>
                <Link itemProp="url" to="/brand" className="text-sm font-bold hover:text-[var(--primary)] transition-all uppercase text-white/60"><span itemProp="name">{t('menu.branding')}</span></Link>
                <Link itemProp="url" to="/privacy" className="text-sm font-bold hover:text-[var(--primary)] transition-all uppercase text-white/60"><span itemProp="name">{t('menu.privacy')}</span></Link>
                <Link itemProp="url" to="/terms" className="text-sm font-bold hover:text-[var(--primary)] transition-all uppercase text-white/60"><span itemProp="name">{t('menu.terms')}</span></Link>
              </div>
            </div>
          </nav>

          <div className="flex flex-col items-center lg:items-start gap-5 min-w-0 text-center lg:text-left lg:order-1">
            <Link to="/" onClick={(e) => { if (location.pathname === '/') { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); } }} className="flex items-center gap-5 group cursor-pointer transition-transform hover:scale-105 active:scale-95 duration-500">
              <div className="w-14 h-14 rounded-2xl bg-white flex items-center justify-center shadow-xl overflow-hidden border border-gray-50 group-hover:shadow-[0_0_30px_rgba(16,185,129,0.3)] transition-all duration-500">
                <img src="/logo.svg" alt="CartLens Logo" className="w-full h-full object-contain p-2" />
              </div>
              <span className="font-outfit font-black text-3xl sm:text-4xl tracking-tight uppercase mt-1.5 text-white">{t('app.title')}</span>
            </Link>
            <p className="text-xl sm:text-2xl leading-snug text-white/65 font-bold uppercase text-balance">
              {t('app.tagline')}
            </p>
            <div className="flex flex-wrap justify-center lg:justify-start gap-5 items-center w-full pt-1">
              <Magnetic>
                <a href="https://x.com/CartLensApp" target="_blank" rel="noopener noreferrer" className="text-white/20 hover:text-[var(--primary)] transition-colors" aria-label="X (Twitter)">
                  <Twitter size={28} />
                  <span className="sr-only">X (Twitter)</span>
                </a>
              </Magnetic>
              <Magnetic>
                <a href="https://www.reddit.com/r/CartLens/" target="_blank" rel="noopener noreferrer" className="text-white/20 hover:text-[var(--primary)] transition-colors" title="Reddit Community" aria-label="Reddit Community">
                  <RedditIcon size={28} />
                  <span className="sr-only">Reddit Community</span>
                </a>
              </Magnetic>
              <Magnetic>
                <a href="https://www.instagram.com/cartlensapp/" target="_blank" rel="noopener noreferrer" className="text-white/20 hover:text-[var(--primary)] transition-colors" title="Instagram" aria-label="Instagram">
                  <Instagram size={28} />
                  <span className="sr-only">Instagram</span>
                </a>
              </Magnetic>
              <Magnetic>
                <a href="https://www.linkedin.com/company/cartlens" target="_blank" rel="noopener noreferrer" className="text-white/20 hover:text-[var(--primary)] transition-colors" aria-label="LinkedIn">
                  <Linkedin size={28} />
                  <span className="sr-only">LinkedIn</span>
                </a>
              </Magnetic>
              <Magnetic>
                <a href="#" target="_blank" rel="noopener noreferrer" className="text-white/20 hover:text-[var(--primary)] transition-colors" aria-label="GitHub">
                  <Github size={28} />
                  <span className="sr-only">GitHub</span>
                </a>
              </Magnetic>
              <Magnetic>
                <a href="https://www.facebook.com/cartlens" target="_blank" rel="noopener noreferrer" className="text-white/20 hover:text-[var(--primary)] transition-colors" title="Facebook" aria-label="Facebook">
                  <Facebook size={28} />
                  <span className="sr-only">Facebook</span>
                </a>
              </Magnetic>
              <Magnetic>
                <a href="https://www.tiktok.com/@cartlens" target="_blank" rel="noopener noreferrer" className="text-white/20 hover:text-[var(--primary)] transition-colors" title="TikTok" aria-label="TikTok">
                  <TikTokIcon size={26} />
                  <span className="sr-only">TikTok</span>
                </a>
              </Magnetic>
              <Magnetic>
                <a href="https://www.youtube.com/@cartlensapp" target="_blank" rel="noopener noreferrer" className="text-white/20 hover:text-[var(--primary)] transition-colors" title="YouTube" aria-label="YouTube">
                  <Youtube size={28} />
                  <span className="sr-only">YouTube</span>
                </a>
              </Magnetic>
              <Magnetic>
                <a href="https://www.producthunt.com/products/cartlens" target="_blank" rel="noopener noreferrer" className="text-white/20 hover:text-[var(--primary)] transition-colors" title="Product Hunt" aria-label="Product Hunt">
                  <ProductHuntIcon size={26} />
                  <span className="sr-only">Product Hunt</span>
                </a>
              </Magnetic>
            </div>

            {/* Dynamic LLM/Neural Node Link */}
            {llmNode && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-0"
              >
                <a
                  href={llmNode.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-[10px] font-mono font-black uppercase tracking-[0.2em] text-[var(--primary)] hover:bg-[var(--primary)] hover:text-white transition-all duration-500 group"
                >
                  <Terminal size={14} className="group-hover:rotate-12 transition-transform" />
                  <span className="normal-case tracking-normal">{llmNode.label}</span>
                  <ArrowRight size={12} className="opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                </a>
              </motion.div>
            )}
          </div>

          {/* Full-width so admin-injected embeds (forms, widgets) aren't
              squeezed into the narrow centered column above. */}
          <CodeSlot
            slotKey="newsletter_footer"
            className="w-full lg:col-span-2 lg:order-3 !bg-transparent !border-none !rounded-none py-0"
          />
        </div>

        <div className="pt-6 border-t border-white/5 flex flex-col sm:flex-row justify-between items-center gap-5 text-center sm:text-left text-white/40 font-mono text-[10px] font-black uppercase tracking-[0.2em]">
          {t('settings.copyright_full').replace('2026', new Date().getFullYear().toString())}
          {/* Privacy choices sit opposite the copyright. */}
          <button type="button" onClick={() => useStore.getState().setConsentPanelOpen(true)} className="font-mono text-[10px] font-black uppercase tracking-[0.2em] text-white/40 hover:text-[var(--primary)] transition-colors">
            {t('cookie_consent.footer_link', { defaultValue: 'Cookie settings / Do Not Sell or Share My Personal Information' })}
          </button>
        </div>
      </div>
    </footer>
    </>
  );
};
